import { createSelector } from '@reduxjs/toolkit'
import { useSelector } from '../../index.ts'
import type { State } from '../../Slice/index.ts'
import type { DraftState, WizardSampleGroup, WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IssueList } from '../../../Services/OrderWizard/issues.ts'
import { collectIssues, currentStep, flaggedGroups, visibleSteps } from '../../../Services/OrderWizard/issues.ts'
import type { UploadSummary } from '../../../Services/OrderWizard/writeState.ts'
import { uploadSummary } from '../../../Services/OrderWizard/writeState.ts'
import { boardItems, sameBoardItems, sameSet } from '../../../Services/OrderWizard/poolBoard.ts'
import { readsAtHand } from '../../../Services/OrderWizard/fileHandles.ts'

type RootState = typeof State

const wizardState = (state: RootState) => state.orderWizard

export const getActiveDraft = (state: RootState): DraftState | null => {
  const { activeDraftId, drafts } = wizardState(state)
  return activeDraftId ? (drafts[activeDraftId] ?? null) : null
}

export const getWizardStorageRestored = (state: RootState) => wizardState(state).storageRestored

export const getUnrestorableDrafts = (state: RootState) => wizardState(state).unrestorable
export const getDroppedDrafts = (state: RootState) => wizardState(state).dropped
export const getDropNotice = (state: RootState) => wizardState(state).dropNotice

/** Drafts newest first, for picking the one to resume. */
export const getDraftsNewestFirst = createSelector([(state: RootState) => wizardState(state).drafts], (drafts) =>
  Object.values(drafts).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
)

export const useActiveDraft = () => useSelector(getActiveDraft)
export const useWizardStorageRestored = () => useSelector(getWizardStorageRestored)
export const useUnrestorableDrafts = () => useSelector(getUnrestorableDrafts)
export const useDroppedDrafts = () => useSelector(getDroppedDrafts)
export const useDropNotice = () => useSelector(getDropNotice)
export const useDraftsNewestFirst = () => useSelector(getDraftsNewestFirst)

/*
 * Per-draft derivations, memoised on the draft object so all readers share one computation per state
 * change. They take the draft, not the root state: the wizard's views get it as a prop.
 */
const theDraft = (draft: DraftState) => draft

/** Whether a group's files can still be read in this browser session. */
export const filesAtHandOf =
  (draft: Pick<DraftState, 'id'>) =>
  (group: WizardSampleGroup): boolean =>
    readsAtHand(draft.id, group)

export const selectDraftIssues = createSelector([theDraft], (draft): IssueList => collectIssues(draft, { filesAtHand: filesAtHandOf(draft) }))

export const selectFileSummary = createSelector([theDraft], (draft): UploadSummary => uploadSummary(draft))

const sameSteps = (a: readonly WizardStep[], b: readonly WizardStep[]) => a.length === b.length && a.every((step, index) => b[index] === step)

/** Stable while the steps stay the same, so effects depending on them do not re-run on every draft change. */
export const selectVisibleSteps = createSelector([theDraft], visibleSteps, { memoizeOptions: { resultEqualityCheck: sameSteps } })

export const selectCurrentStep = createSelector([theDraft, selectVisibleSteps], currentStep)

/** Stable while what the board shows of the items stays the same, so its memoised columns and rows hold. */
export const selectBoardItems = createSelector([theDraft], boardItems, { memoizeOptions: { resultEqualityCheck: sameBoardItems } })

export const selectFlaggedGroups = createSelector(
  [(issues: Pick<IssueList, 'blockers' | 'warnings'>) => issues, (_: unknown, step: WizardStep) => step],
  flaggedGroups,
  { memoizeOptions: { resultEqualityCheck: sameSet } }
)

const NO_ISSUES: IssueList = { blockers: [], warnings: [], infos: [] }

export const selectActiveDraftIssues = (state: RootState): IssueList => {
  const draft = getActiveDraft(state)
  return draft ? selectDraftIssues(draft) : NO_ISSUES
}

export const selectActiveFileSummary = (state: RootState): UploadSummary | undefined => {
  const draft = getActiveDraft(state)
  return draft ? selectFileSummary(draft) : undefined
}

export const useActiveFileSummary = () => useSelector(selectActiveFileSummary)
